/*
 * RIGHT PANEL COMPONENT
 * 
 * Container component for the right side panel with contextual content.
 * 
 * Features:
 * - Shows ordinary Chat or editor content on every route.
 * - Uses CSS display:none to preserve content state when switching
 * - Each content type (Chat, KanbanEditor, etc.) provides its own header
 * - No tab bar - panel shows one thing at a time based on what opened it
 * 
 * Design Decision:
 * Tabs were removed in favor of a simpler contextual model:
 * - Navbar Chat button -> opens ordinary Chat.
 * - Kanban gear button -> opens Kanban Editor (with its own header)
 * - User clicks respective trigger to switch content
 * 
 * Props:
 * - onClose: Callback to close the panel
 * - onOpenFullPage: Callback to open chat in full-page mode
 *
 * Recent changes:
 * - July 30, 2026 - Keeps embedded content inside the mobile panel shrink boundary.
 */

import { FC, useCallback, useEffect } from 'react';
import { useChatPanel } from '../../context/ChatPanelContext';
import { useChatContext } from '../../context/ChatContext';
import Chat from '../../pages/Chat';
import EditorContainer from './EditorContainer';

interface RightPanelProps {
  onClose: () => void;
  onOpenFullPage: () => void;
}

const RightPanel: FC<RightPanelProps> = ({ onClose, onOpenFullPage }) => {
  const { activeTab } = useChatContext();
  const panel = useChatPanel();
  const setChatReady = panel?.setChatReady;
  const onChatReady = useCallback(() => setChatReady?.(true), [setChatReady]);
  useEffect(() => () => setChatReady?.(false), [setChatReady]);

  return (
    <div className="flex h-full min-w-0 max-w-full flex-col">
      {/* Content Area - uses display to preserve state */}
      <div className="min-w-0 max-w-full flex-1 overflow-hidden">
        <div
          className="h-full min-w-0 max-w-full flex-col"
          style={{ display: activeTab === 'chat' ? 'flex' : 'none' }}
        >
          <div className="min-h-0 flex-1"><Chat
            onReady={panel ? onChatReady : undefined}
            onClose={onClose}
            onOpenFullPage={onOpenFullPage}
            showSessionSidebar={false}
          /></div>
        </div>

        {/* Editor Content */}
        <div
          className="h-full min-w-0 max-w-full"
          style={{ display: activeTab === 'editor' ? 'block' : 'none' }}
        >
          <EditorContainer onClose={onClose} />
        </div>
      </div>
    </div>
  );
};

export default RightPanel;
